import { useAuth, useUser } from "@clerk/clerk-react";
import { useEffect } from "react";
import { setOfflineSession } from "@/lib/offlineSession";

/**
 * Persists a display-only identity snapshot to IDB whenever the user is
 * signed-in. Runs on mount, window focus, and reconnect so the 5-day sliding
 * TTL stays fresh during active sessions (spec §4 AC-F2).
 *
 * Must only be mounted when Clerk is loaded and the user is signed-in.
 * No tokens are ever written to IDB — only userId/email/name/sessionId.
 */
export function useClerkSessionPersister(): void {
	const { user } = useUser();
	const { sessionId } = useAuth();

	useEffect(() => {
		function persist() {
			if (!user || !sessionId) return;
			setOfflineSession({
				userId: user.id,
				email: user.primaryEmailAddress?.emailAddress ?? "",
				name:
					user.fullName ||
					[user.firstName, user.lastName].filter(Boolean).join(" ") ||
					"",
				sessionId,
			}).catch(() => {
				// IDB write failure is non-fatal; the user is online so this
				// doesn't break anything — just means offline bypass won't work.
			});
		}

		persist();

		window.addEventListener("focus", persist);
		window.addEventListener("online", persist);
		return () => {
			window.removeEventListener("focus", persist);
			window.removeEventListener("online", persist);
		};
	}, [user, sessionId]);
}
